"use client";

import React, { useEffect, useMemo } from "react";
import { useParams } from "next/navigation";
import Image from "next/image";
import { useHeader } from "@/context/HeaderLayoutContext";
import { useFetchEcompage } from "@/apis/queryFunctions/ecompagesFunctions";
import { parseHtmlJson } from "@/utils/parseHtmlTextToJson";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";
import { Title, Description } from "@/components/Common/Typography";
import noidiaData from "@/mockDatas/noidiaData.json";

// Components
import VietnamMap from "./components/VietnamMap";
import NewsSection from "@/components/Homepage/NewsSection/NewsSection";
import PartnerSection from "@/components/Homepage/PartnerSection/PartnerSection";

// Styles
import styles from "./noidia.module.css";

// Icons
import {
  LucideIcon,
  Leaf,
  MapPin,
  Store,
  TrendingUp,
  ShieldCheck,
  Truck,
  ShoppingCart,
  Handshake,
} from "lucide-react";

const iconMap: Record<string, LucideIcon> = {
  Leaf,
  Store,
  TrendingUp,
  ShieldCheck,
  Handshake,
  MapPin,
  Truck,
  ShoppingCart,
};

export default function NoiDiaPage() {
  const params = useParams();
  const locale = (params?.locale as "vi" | "en") || "vi";
  const { setHeaderStyle } = useHeader();

  const { data: pageData } = useFetchEcompage(
    "trang-noi-dia-website-viet-thien",
    locale
  );

  useEffect(() => {
    setHeaderStyle({ transparent: true });
    return () => setHeaderStyle({ transparent: false });
  }, [setHeaderStyle]);

  const actualPageData = pageData?.root || pageData;

  // Mock data fallback for Noi Dia
  const pageMock = (noidiaData as any)[locale] || (noidiaData as any).vi;
  const heroMock = pageMock.hero;
  const distMock = pageMock.distribution;

  // #region Add-on Data Processing
  const allAddOn1 = useMemo(() => {
    const list =
      Array.isArray(actualPageData?.add_on_data) &&
      actualPageData.add_on_data.length > 0
        ? actualPageData.add_on_data
        : [];
    return list;
  }, [actualPageData]);

  const allAddOn2 = useMemo(() => {
    const list2 =
      Array.isArray(actualPageData?.add_on_data_2) &&
      actualPageData.add_on_data_2.length > 0
        ? actualPageData.add_on_data_2
        : [];
    return list2;
  }, [actualPageData]);

  // 1. Banner Data (Hero Section)
  const bannerItem = useMemo(() => {
    return (
      allAddOn1.find((item: any) => {
        const title = (item?.title || "").toLowerCase();
        return title === "banner" || title.includes("banner");
      }) || null
    );
  }, [allAddOn1]);

  const bannerJson = useMemo(() => {
    if (!bannerItem?.content) return null;
    return parseHtmlJson(bannerItem.content);
  }, [bannerItem]);

  const heroData = useMemo(() => {
    const bgImage = bannerItem?.picture
      ? appendImageUrlFromAPI({ src: bannerItem.picture, size: "2k" })
      : actualPageData?.picture
        ? appendImageUrlFromAPI({ src: actualPageData.picture, size: "2k" })
        : heroMock.bgImage;

    return {
      bgImage,
      title: bannerJson?.title || heroMock.title,
      description: bannerJson?.description || heroMock.description,
      stats: bannerJson?.stats || heroMock.stats || [],
    };
  }, [bannerItem, bannerJson, actualPageData, heroMock]);

  // 2. Distribution Data
  const distItem = useMemo(() => {
    return (
      allAddOn1.find((item: any) => {
        const title = (item?.title || "").toLowerCase();
        return (
          title === "he-thong-phan-phoi" ||
          title === "distribution" ||
          title.includes("phan-phoi")
        );
      }) || null
    );
  }, [allAddOn1]);

  const distJson = useMemo(() => {
    if (!distItem?.content) return null;
    return parseHtmlJson(distItem.content);
  }, [distItem]);

  const distData = useMemo(() => {
    const distParsed = distJson?.distribution || distJson;
    return {
      title: distParsed?.title || distMock.title,
      paragraphs: distParsed?.paragraphs || distMock.paragraphs || [],
      coreValues: distParsed?.coreValues || distMock.coreValues || [],
      mapLabels: distParsed?.mapLabels ||
        distMock.mapLabels || {
          hoangSaText:
            locale === "vi" ? "QUẦN ĐẢO HOÀNG SA" : "PARACEL ISLANDS",
          hoangSaSub: locale === "vi" ? "(VIỆT NAM)" : "(VIETNAM)",
          truongSaText:
            locale === "vi" ? "QUẦN ĐẢO TRƯỜNG SA" : "SPRATLY ISLANDS",
          truongSaSub: locale === "vi" ? "(VIỆT NAM)" : "(VIETNAM)",
        },
    };
  }, [distJson, distMock, locale]);

  // 3. Partner & Certification Data
  const partnerData = useMemo(() => {
    return (
      allAddOn1.find((item: any) => {
        const title = (item?.title || "").toLowerCase();
        const summary = (item?.summary || "").toLowerCase();
        return ["doi-tac-chung-nhan", "doi-tac"].some(
          (k) => title === k || summary === k || title.includes(k)
        );
      }) || null
    );
  }, [allAddOn1]);

  const partnerHeaders = useMemo(() => {
    let parsed: any = null;
    try {
      const rawContent = partnerData?.content || "";
      parsed = parseHtmlJson(rawContent);
    } catch (error) {
      console.error("Error parsing partner headers JSON:", error);
    }
    return {
      partners: parsed?.partners || {
        title: locale === "vi" ? "ĐỐI TÁC CỦA CHÚNG TÔI" : "OUR PARTNERS",
        desc:
          locale === "vi"
            ? "Chúng tôi tự hào hợp tác cùng các thương hiệu uy tín hàng đầu Việt Nam và quốc tế."
            : "We are proud to partner with leading reputable brands in Vietnam and internationally.",
      },
      certifications: parsed?.certifications || {
        title:
          locale === "vi" ? "CHỨNG NHẬN CHẤT LƯỢNG" : "QUALITY CERTIFICATIONS",
        desc:
          locale === "vi"
            ? "Các chứng nhận chất lượng sản phẩm, đảm bảo uy tín và niềm tin cho đối tác."
            : "Product quality certifications, ensuring reputation and trust for partners.",
      },
    };
  }, [partnerData, locale]);

  const partnerLogos = useMemo(() => {
    return allAddOn2
      .filter(
        (item: any) =>
          (item?.title || "").toLowerCase() === "doi-tac" &&
          item?.shared !== false &&
          item?.shared !== "false"
      )
      .flatMap((item: any) => [
        item.picture,
        item.picture_2,
        item.picture_3,
        item.picture_4,
        item.picture_5,
        item.picture_6,
      ])
      .filter(Boolean);
  }, [allAddOn2]);

  const certificationImages = useMemo(() => {
    return allAddOn2
      .filter(
        (item: any) =>
          (item?.title || "").toLowerCase() === "chung-nhan" &&
          item?.shared !== false &&
          item?.shared !== "false"
      )
      .flatMap((item: any) => [
        item.picture,
        item.picture_2,
        item.picture_3,
        item.picture_4,
        item.picture_5,
        item.picture_6,
      ])
      .filter(Boolean);
  }, [allAddOn2]);

  //#region News
  const newsData = useMemo(() => {
    return (
      allAddOn1.find((item: any) => {
        const title = (item?.title || "").toLowerCase();
        const summary = (item?.summary || "").toLowerCase();
        return (
          title === "tin-tuc" ||
          summary === "tin-tuc" ||
          title.includes("tin-tuc")
        );
      }) || null
    );
  }, [allAddOn1]);

  const newsHeader = useMemo(() => {
    return {
      title:
        newsData?.summary ||
        (locale === "vi" ? "TIN TỨC MỚI NHẤT" : "LATEST NEWS"),
      subtitle:
        newsData?.content_3 ||
        (locale === "vi"
          ? "Cập nhật thông tin nổi bật mới nhất"
          : "Stay updated with the latest news"),
      decoImage: newsData?.picture || "",
    };
  }, [newsData, locale]);
  //#endregion

  // Helper to dynamically highlight milestone statistics keywords
  const renderDistP2 = (text: string) => {
    if (!text) return null;
    const highlightWords = [
      "1 triệu sản phẩm",
      "1 million products",
      "1 million",
      "1 triệu",
    ];
    for (const kw of highlightWords) {
      if (text.includes(kw)) {
        const parts = text.split(kw);
        return (
          <>
            {parts[0]}
            <strong>{kw}</strong>
            {parts[1]}
          </>
        );
      }
    }
    return text;
  };

  return (
    <div className={styles.wrapper}>
      {/* ================= HERO SECTION ================= */}
      <section className={styles.hero}>
        {heroData.bgImage && (
          <Image
            src={heroData.bgImage}
            alt="Viet Thien Domestic Distribution"
            fill
            priority
            sizes="100vw"
            className={styles.heroBgImage}
          />
        )}

        <div className={styles.heroContainer}>
          <div className={styles.textBlock}>
            {/* Standard Typography Title */}
            <Title
              size="medium"
              as="h1"
              color="#ffffff"
              align="left"
              style={{
                marginBottom: "14px",
                textShadow: "0 2px 10px rgba(0, 0, 0, 0.45)",
              }}
            >
              {heroData.title}
            </Title>

            {/* Standard Typography Description */}
            <Description
              as="p"
              size="medium"
              color="rgba(255, 255, 255, 0.95)"
              align="left"
              className={styles.heroDesc}
              style={{
                textShadow: "0 1px 5px rgba(0, 0, 0, 0.35)",
                lineHeight: 1.7,
              }}
            >
              {heroData.description}
            </Description>

            {/* Stats List */}
            {heroData.stats && heroData.stats.length > 0 && (
              <div className={styles.statsList}>
                {heroData.stats.map(
                  ({ num, unit, label, icon }: any, i: number) => {
                    const Icon = iconMap[icon] || Leaf;
                    return (
                      <div className={styles.statCard} key={i}>
                        <div className={styles.statIconWrapper}>
                          <Icon size={18} />
                        </div>
                        <div className={styles.statInfo}>
                          <div className={styles.statNumberWrapper}>
                            <span className={styles.statNumber}>{num}</span>
                            <span className={styles.statUnit}>{unit}</span>
                          </div>
                          <span className={styles.statLabel}>{label}</span>
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ================= DISTRIBUTION SECTION ================= */}
      <section id="general-trade" className={styles.distributionSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.distributionGrid}>
            {/* Left Content */}
            <div className={styles.distLeft}>
              <Title
                size="medium"
                as="h2"
                color="#072917"
                className={styles.sectionTitle}
              >
                {distData.title}
              </Title>
              <div className={styles.descriptionParagraphs}>
                {distData.paragraphs?.map((p: string, idx: number) => (
                  <Description
                    key={idx}
                    as="p"
                    size="medium"
                    className={styles.distParagraph}
                  >
                    {idx === 1 ? renderDistP2(p) : p}
                  </Description>
                ))}
              </div>

              {/* Core Values (Horizontal) */}
              <div className={styles.valuesHorizontalList}>
                {distData.coreValues?.map(
                  ({ title, desc, icon }: any, i: number) => {
                    const Icon = iconMap[icon] || Leaf;
                    return (
                      <div className={styles.valueHorizontalItem} key={i}>
                        <div className={styles.valueCircleIcon}>
                          <Icon size={24} />
                        </div>
                        <span className={styles.valueItemTitle}>{title}</span>
                        <span className={styles.valueItemDesc}>{desc}</span>
                      </div>
                    );
                  }
                )}
              </div>
            </div>

            {/* Middle Map (Green Variant) */}
            <div className={styles.distCenter}>
              <div className={styles.greenMapBox}>
                <VietnamMap
                  variant="green"
                  customLabels={{
                    hoangSaText: distData.mapLabels?.hoangSaText,
                    hoangSaSub: distData.mapLabels?.hoangSaSub,
                    truongSaText: distData.mapLabels?.truongSaText,
                    truongSaSub: distData.mapLabels?.truongSaSub,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= PARTNERS & CERTIFICATES SECTION ================= */}
      <div className="pt-24 pb-20">
        <PartnerSection
          isActive={true}
          partnerLogos={partnerLogos}
          certificationImages={certificationImages}
          partnerHeader={partnerHeaders.partners}
          certHeader={partnerHeaders.certifications}
          locale={locale}
        />
      </div>

      {/* ================= NEWS SECTION ================= */}
      <NewsSection
        isActive={true}
        locale={locale}
        title={newsHeader.title}
        subtitle={newsHeader.subtitle}
        decoImage={newsHeader.decoImage}
      />
    </div>
  );
}
